import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";
interface BackupRun {
  id: string;
  inizio: string;
  fine: string | null;
  esito: "success" | "failure";
  s3_key: string | null;
  size_bytes: number | null;
  durata_secondi: string | null;
  triggered_by_user_id: string | null;
  error: string | null;
}

interface RunBackupResponse {
  success: boolean;
  s3_key: string | null;
  size_bytes: number;
  duration_seconds: number;
  error: string | null;
}

export function BackupAdminPage() {
  useAuth(); // session check
  const qc = useQueryClient();

  const { data: runs, isLoading } = useQuery({
    queryKey: ["admin", "backup", "runs"],
    queryFn: async () => (await api.get<BackupRun[]>("/admin/backup/runs")).data,
  });

  const runMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<RunBackupResponse>("/admin/backup/run");
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "backup", "runs"] });
      if (data.success) {
        alert(
          `Backup OK → ${data.s3_key}\n${data.size_bytes.toLocaleString()} bytes in ${data.duration_seconds.toFixed(1)}s`,
        );
      } else {
        alert(tr("Backup FAILED: {{v1}}", { v1: data.error }));
      }
    },
  });

  return (
    <MobileShell logoSuffix="BACKUP">
      <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Backup esterno S3")}</h2>

      <div className="card">
        <button onClick={() => runMut.mutate()} disabled={runMut.isPending} className="btn-primary">
          {runMut.isPending
            ? tr("Backup in corso (può richiedere minuti)…")
            : tr("▶ Esegui backup ora")}
        </button>
      </div>

      <div className="card p-0 overflow-hidden">
        <h3 className="font-display text-lg text-accent p-4 border-b border-primary/10">
          {tr("Ultimi run")}
        </h3>
        {isLoading && <div className="p-4 text-fg-muted">{tr("Caricamento…")}</div>}
        {runs?.length === 0 && (
          <div className="p-6 text-center text-fg-muted">{tr("Nessun backup eseguito.")}</div>
        )}
        {runs?.map((r) => (
          <div
            key={r.id}
            className="flex items-center gap-4 p-3 border-b border-primary/10 last:border-b-0"
          >
            <span
              className={`pill ${
                r.esito === "success" ? "bg-success/20 text-success" : "bg-danger/20 text-danger"
              }`}
            >
              {etichetta(r.esito)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm">
                {new Date(r.inizio).toLocaleString(localeCorrente())} ·{" "}
                {r.durata_secondi ? `${r.durata_secondi}s` : "—"}
              </div>
              {r.s3_key && (
                <div className="text-xs text-fg-muted font-mono mt-0.5">
                  {r.s3_key} ·{" "}
                  {r.size_bytes
                    ? tr("{{v1}} MB", { v1: (r.size_bytes / 1024 / 1024).toFixed(2) })
                    : "—"}
                </div>
              )}
              {r.error && <div className="text-xs text-danger font-mono mt-0.5">{r.error}</div>}
            </div>
          </div>
        ))}
      </div>
    </MobileShell>
  );
}
